import React, { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon, BuildingIcon, PackageIcon, UserIcon, MonitorIcon, PlusIcon, PencilIcon, TrashIcon, CheckIcon, GlobeIcon, FileTextIcon, ScaleIcon, PenLineIcon, CreditCardIcon, SmartphoneIcon, StoreIcon, ArrowRightIcon, ClockIcon, RefreshCwIcon, SparklesIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { RESOURCES, USAGE_TYPES, CONTRACT_TEMPLATES } from '../../data/windsor';
import type { ResourceKind, UsageType } from '../../types/loisirs';
const KIND_ICON: Record<ResourceKind, typeof BuildingIcon> = {
  plateau: BuildingIcon,
  equipement: PackageIcon,
  humain: UserIcon,
  enligne: MonitorIcon
};
const KIND_LABEL: Record<ResourceKind, {
  fr: string;
  en: string;
}> = {
  plateau: {
    fr: 'Plateau',
    en: 'Facility'
  },
  equipement: {
    fr: 'Équipement',
    en: 'Equipment'
  },
  humain: {
    fr: 'Ressource humaine',
    en: 'Human resource'
  },
  enligne: {
    fr: 'En ligne',
    en: 'Online'
  }
};

// --- RESOURCES --------------------------------------------------------------

export function ResourcesPanel() {
  const {
    t,
    lang
  } = useLanguage();
  const [expanded, setExpanded] = useState<string[]>(['res-lemay', 'res-rl']);
  const locale = lang === 'fr' ? 'fr-CA' : 'en-CA';
  const money = (value: number) => value.toLocaleString(locale, {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0
  });
  const parents = RESOURCES.filter(r => !r.parentId);
  const childrenOf = (id: string) => RESOURCES.filter(r => r.parentId === id);
  const Row = ({
    resource,
    depth
  }: {
    resource: (typeof RESOURCES)[number];
    depth: number;
  }) => {
    const kids = childrenOf(resource.id);
    const open = expanded.includes(resource.id);
    const Icon = KIND_ICON[resource.kind];
    return <>
        <li className="group flex items-center gap-2 rounded-lg px-2 py-2.5 hover:bg-slate-50 transition-colors" style={{
        paddingLeft: 8 + depth * 22
      }} data-id="element-2957" data-mp="2rn5bfisu3ou3">
          {kids.length > 0 ? <button onClick={() => setExpanded(prev => open ? prev.filter(id => id !== resource.id) : [...prev, resource.id])} aria-label={open ? t('Replier', 'Collapse') : t('Déplier', 'Expand')} aria-expanded={open} className="p-0.5 rounded text-slate-400 hover:text-slate-700 shrink-0" data-id="element-2958" data-mp="prpgbwtt5qyh">
              {open ? <ChevronDownIcon className="w-4 h-4" data-id="element-2959" data-mp="tsj48q7935rd" /> : <ChevronRightIcon className="w-4 h-4" data-id="element-2960" data-mp="ysdxh0ei07fl" />}
            </button> : <span className="w-5 shrink-0" data-id="element-2961" data-mp="3eja4o1mliwg1" />}

          <span className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${resource.kind === 'plateau' ? 'bg-indigo-50 text-[#4F46E5]' : resource.kind === 'equipement' ? 'bg-amber-50 text-amber-600' : resource.kind === 'humain' ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'}`} data-id="element-2962" data-mp="3elwds5p4xk0d">
            <Icon className="w-3.5 h-3.5" data-id="element-2963" data-mp="26kulga3llkzj" />
          </span>

          <div className="min-w-0 flex-1" data-id="element-2964" data-mp="1iu8nxcr7zj3s">
            <p className="text-sm font-medium text-slate-800 truncate" data-id="element-2965" data-mp="1iva41vn3evsd">
              {resource.name[lang]}
              {!resource.bookable && <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-[10px] font-semibold uppercase" data-id="element-2966" data-mp="1rmtwy151ubff">
                  {t('regroupement', 'grouping')}
                </span>}
            </p>
            <p className="text-xs text-slate-400 truncate" data-id="element-2967" data-mp="z5pwspmnlvmg">
              {KIND_LABEL[resource.kind][lang]}
              {resource.address && ` · ${resource.address}`}
              {resource.capacity ? ` · ${resource.capacity} pl.` : ''}
              {resource.bookable && resource.rateCitizen !== undefined ? ` · ${resource.rateCitizen === 0 ? t('gratuit', 'free') : `${money(resource.rateCitizen)}/h`}` : ''}
            </p>
          </div>

          <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity" data-id="element-2968" data-mp="1rio2enkh2o87">
            <button aria-label={t('Modifier la ressource', 'Edit resource')} className="p-2 rounded-lg text-slate-400 hover:text-[#4F46E5] hover:bg-indigo-50 transition-colors" data-id="element-2969" data-mp="1viog25zqyve7">
              <PencilIcon className="w-4 h-4" data-id="element-2970" data-mp="25hdyq0zgwjvi" />
            </button>
            <button aria-label={t('Retirer la ressource', 'Remove resource')} className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors" data-id="element-2971" data-mp="6635rn5l2x19">
              <TrashIcon className="w-4 h-4" data-id="element-2972" data-mp="3m6gi6wkdrb3" />
            </button>
          </div>
        </li>
        {open && kids.map(kid => <Row key={kid.id} resource={kid} depth={depth + 1} data-id="element-2973" data-mp="3q07rmn0gls05" />)}
      </>;
  };
  const groups: {
    kind: ResourceKind;
    title: string;
  }[] = [{
    kind: 'plateau',
    title: t('Plateaux et lieux', 'Facilities and venues')
  }, {
    kind: 'equipement',
    title: t('Équipements prêtables', 'Loanable equipment')
  }, {
    kind: 'humain',
    title: t('Ressources humaines', 'Human resources')
  }, {
    kind: 'enligne',
    title: t('En ligne', 'Online')
  }];
  return <section className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-2974" data-mp="cjgtbwp15oi4">
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap" data-id="element-2975" data-mp="7lf9h9js5l5l">
        <div data-id="element-2976" data-mp="2xbsooyulr056">
          <h2 className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-2977" data-mp="2xclkj4s0w58j">
            {t('Ressources', 'Resources')}
          </h2>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl" data-id="element-2978" data-mp="2sb4q9s41nh0x">
            {t('Tout ce qui peut être réservé : plateaux, salles, équipements, personnel et créneaux en ligne. Les salles se rattachent au bâtiment qui les contient.', 'Everything that can be booked: facilities, rooms, equipment, staff and online slots. Rooms attach to the building that contains them.')}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap" data-id="element-2979" data-mp="10p9jakuzoczw">
          {[{
          kind: 'plateau' as const,
          label: t('Ajouter un plateau', 'Add a facility')
        }, {
          kind: 'equipement' as const,
          label: t('Ajouter un équipement', 'Add equipment')
        }, {
          kind: 'humain' as const,
          label: t('Ajouter une ressource humaine', 'Add a human resource')
        }].map((action, i) => <button key={action.kind} className={`inline-flex items-center gap-2 px-3.5 py-2 text-sm font-semibold rounded-lg transition-colors ${i === 0 ? 'bg-[#4F46E5] hover:bg-[#4338CA] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-id="element-2980" data-mp="hqow33w7xrrj" data-mpi={i}>
              <PlusIcon className="w-4 h-4" data-id="element-2981" data-mp="d2ffwiur5jk3" />
              {action.label}
            </button>)}
        </div>
      </div>

      <div className="space-y-5" data-id="element-2982" data-mp="84m9bopjenja">
        {groups.map(group => {
        const items = parents.filter(r => r.kind === group.kind);
        if (items.length === 0) return null;
        return <div key={group.kind} data-id="element-2983" data-mp="hywey83hpiby">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5" data-id="element-2984" data-mp="vsdn6wqq78mo">
                {group.title}
                <span className="ml-2 text-slate-300" data-id="element-2985" data-mp="qtkle1ksx62c">{items.length}</span>
              </h3>
              <ul className="divide-y divide-slate-100 border border-slate-100 rounded-lg" data-id="element-2986" data-mp="15r39d1pnnf8q">
                {items.map(resource => <Row key={resource.id} resource={resource} depth={0} data-id="element-2987" data-mp="10pdt97nksbds" />)}
              </ul>
            </div>;
      })}
      </div>
    </section>;
}

// --- USAGE TYPES ------------------------------------------------------------

const PALETTE = ['#4F46E5', '#0284C7', '#DB2777', '#7C3AED', '#0D9488', '#B45309', '#CA8A04', '#059669', '#9333EA', '#2563EB', '#475569', '#64748B'];
export function UsageTypesPanel() {
  const {
    t,
    lang
  } = useLanguage();
  const [types, setTypes] = useState<UsageType[]>(USAGE_TYPES);
  const [picking, setPicking] = useState<string | null>(null);
  const update = (id: string, patch: Partial<UsageType>) => setTypes(prev => prev.map(u => u.id === id ? {
    ...u,
    ...patch
  } : u));
  return <section className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-2988" data-mp="3dk004hia2szl">
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap" data-id="element-2989" data-mp="2g2i0wdg9edkq">
        <div data-id="element-2990" data-mp="2l1ksow78anar">
          <h2 className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-2991" data-mp="2l131idzfaz9p">
            {t('Types d’utilisation', 'Usage types')}
          </h2>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl" data-id="element-2992" data-mp="124x585oftwvc">
            {t('Le type d’utilisation pilote la couleur au calendrier, le contrat proposé par défaut, la tarification et la visibilité publique de la plage.', 'The usage type drives the calendar colour, the default contract, the pricing and whether the slot is publicly visible.')}
          </p>
        </div>
        <button className="inline-flex items-center gap-2 px-3.5 py-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-lg transition-colors shrink-0" data-id="element-2993" data-mp="ehxf5malu57o">
          <PlusIcon className="w-4 h-4" data-id="element-2994" data-mp="adzcj1m2mc9v" />
          {t('Nouveau type', 'New type')}
        </button>
      </div>

      <ul className="space-y-2" data-id="element-2995" data-mp="hweo7y94zsq">
        {types.map(usage => <li key={usage.id} className="rounded-lg border border-slate-200 px-4 py-3.5" data-id="element-2996" data-mp="h4iauwtyy1e">
            <div className="flex items-start gap-3 flex-wrap" data-id="element-2997" data-mp="3rqjr1yeilco5">
              {/* Colour */}
              <div className="relative shrink-0" data-id="element-2998" data-mp="2upddjsxu6cvq">
                <button onClick={() => setPicking(picking === usage.id ? null : usage.id)} aria-label={t('Changer la couleur', 'Change colour')} className="w-8 h-8 rounded-lg border-2 border-white ring-1 ring-slate-200" style={{
              backgroundColor: usage.color
            }} data-id="element-2999" data-mp="2prk9p6k09uym" />
                {picking === usage.id && <div className="absolute z-10 mt-1 grid grid-cols-6 gap-1 p-2 bg-white rounded-lg border border-slate-200 shadow-lg" data-id="element-3000" data-mp="26vlutrfy9t9j">
                    {PALETTE.map(color => <button key={color} onClick={() => {
                update(usage.id, {
                  color
                });
                setPicking(null);
              }} aria-label={color} className="w-6 h-6 rounded-md ring-1 ring-slate-200 flex items-center justify-center" style={{
                backgroundColor: color
              }} data-id="element-3001" data-mp="2fsw4j8d4dacc">
                        {usage.color === color && <CheckIcon className="w-3.5 h-3.5 text-white" data-id="element-3002" data-mp="3pujrkd5mgszx" />}
                      </button>)}
                  </div>}
              </div>

              <div className="min-w-0 flex-1" data-id="element-3003" data-mp="me2wcemikgjc">
                <p className="text-sm font-medium text-slate-800" data-id="element-3004" data-mp="r9rzst1nmhkx">
                  {usage.label[lang]}
                </p>
                <div className="flex items-center gap-3 mt-1.5 flex-wrap" data-id="element-3005" data-mp="clnd7dzhdlwv">
                  <label className="sr-only" htmlFor={`tpl-${usage.id}`} data-id="element-3006" data-mp="hjp6ssuj93ml">
                    {t('Contrat par défaut', 'Default contract')}
                  </label>
                  <select id={`tpl-${usage.id}`} value={usage.defaultContractTemplateId ?? ''} onChange={e => update(usage.id, {
                defaultContractTemplateId: e.target.value || undefined
              })} className="px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs bg-white focus:outline-none focus:ring-1 focus:ring-[#4F46E5]" data-id="element-3007" data-mp="1a11a34j91adk">
                    <option value="" data-id="element-3008" data-mp="10yp3vqw1yqid">
                      {t('Aucun contrat', 'No contract')}
                    </option>
                    {CONTRACT_TEMPLATES.map(tpl => <option key={tpl.id} value={tpl.id} data-id="element-3009" data-mp="r8ck40w2b1da">
                        {tpl.name[lang]}
                      </option>)}
                  </select>

                  <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden" data-id="element-3010" data-mp="m7olat1ldg8i">
                    {[{
                  id: 'gratuit' as const,
                  label: t('Gratuit', 'Free')
                }, {
                  id: 'tarif' as const,
                  label: t('Tarifé', 'Charged')
                }].map(option => <button key={option.id} onClick={() => update(usage.id, {
                  defaultPricing: option.id
                })} className={`px-2.5 py-1.5 text-xs font-medium transition-colors ${usage.defaultPricing === option.id ? 'bg-[#0F172A] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-id="element-3011" data-mp="2ttga1n45d3mb">
                        {option.label}
                      </button>)}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0" data-id="element-3012" data-mp="1mhfu9dbm4y68">
                <span className="inline-flex items-center gap-1.5 text-xs text-slate-500" data-id="element-3013" data-mp="1rg0z5q1t8610">
                  <GlobeIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-3014" data-mp="3u2odxr9m9rnf" />
                  {usage.showOnPublicCalendar ? t('public', 'public') : t('interne', 'internal')}
                </span>
                <button type="button" role="switch" aria-checked={usage.showOnPublicCalendar} aria-label={t('Visible sur les calendriers publics', 'Visible on public calendars')} onClick={() => update(usage.id, {
              showOnPublicCalendar: !usage.showOnPublicCalendar
            })} className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:ring-offset-1 ${usage.showOnPublicCalendar ? 'bg-[#4F46E5]' : 'bg-slate-200'}`} data-id="element-3015" data-mp="6ryhse55cqj9">
                  <span aria-hidden="true" className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${usage.showOnPublicCalendar ? 'translate-x-4' : 'translate-x-0'}`} data-id="element-3016" data-mp="2ddgi77wlrat2" />
                </button>
              </div>
            </div>
          </li>)}
      </ul>
    </section>;
}

// --- CONTRACT TEMPLATES -----------------------------------------------------

export function ContractsPanel() {
  const {
    t,
    lang
  } = useLanguage();
  const [templates, setTemplates] = useState(CONTRACT_TEMPLATES);
  return <section className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-3017" data-mp="3f0g7q8b54whj">
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap" data-id="element-3018" data-mp="3f17v4vul3nwq">
        <div data-id="element-3019" data-mp="3jy7w4scku0gz">
          <h2 className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-3020" data-mp="3jyrgsgtm32yp">
            {t('Modèles de contrats', 'Contract templates')}
          </h2>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl" data-id="element-3021" data-mp="3diradbhtq9kb">
            {t('Un modèle par type de location. Le greffe rédige certaines ententes hors plateforme : elles sont alors importées au dossier plutôt que générées.', 'One template per rental type. The clerk’s office drafts some agreements off-platform: those are imported into the file rather than generated.')}
          </p>
        </div>
        <button className="inline-flex items-center gap-2 px-3.5 py-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-lg transition-colors shrink-0" data-id="element-3022" data-mp="4w9m8ebysq7">
          <PlusIcon className="w-4 h-4" data-id="element-3023" data-mp="3mixngw9m17iy" />
          {t('Nouveau modèle', 'New template')}
        </button>
      </div>

      <ul className="space-y-2" data-id="element-3024" data-mp="nvjk91fmrwgg">
        {templates.map(tpl => <li key={tpl.id} className="group flex items-start gap-3 rounded-lg border border-slate-200 px-4 py-3.5" data-id="element-3025" data-mp="j2yss6k75vg3">
            <span className="w-8 h-8 rounded-lg bg-indigo-50 text-[#4F46E5] flex items-center justify-center shrink-0" data-id="element-3026" data-mp="qghjeitxjimr">
              <FileTextIcon className="w-4 h-4" data-id="element-3027" data-mp="10cudvixau0ik" />
            </span>

            <div className="min-w-0 flex-1" data-id="element-3028" data-mp="10enn95zaqyu9">
              <p className="text-sm font-medium text-slate-800" data-id="element-3029" data-mp="15c732abn7job">
                {tpl.name[lang]}
              </p>
              <p className="text-xs text-slate-500 mt-0.5" data-id="element-3030" data-mp="19c7rmnsghb93">
                {tpl.scope[lang]} · {tpl.clauses}{' '}
                {t('clauses', 'clauses')}
              </p>
              <div className="flex items-center gap-2 mt-2 flex-wrap" data-id="element-3031" data-mp="1j2iu05whrcdv">
                <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden" data-id="element-3032" data-mp="1j5ethynzm94f">
                  {[{
                id: 'loisirs' as const,
                label: t('Loisirs', 'Recreation'),
                icon: PenLineIcon
              }, {
                id: 'greffe' as const,
                label: t('Greffe', 'Clerk'),
                icon: ScaleIcon
              }].map(option => {
                const Icon = option.icon;
                return <button key={option.id} onClick={() => setTemplates(prev => prev.map(item => item.id === tpl.id ? {
                  ...item,
                  author: option.id
                } : item))} className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium transition-colors ${tpl.author === option.id ? 'bg-[#0F172A] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-id="element-3033" data-mp="2ebx5walnpm5q">
                        <Icon className="w-3 h-3" data-id="element-3034" data-mp="1bxg5x55gy1jp" />
                        {option.label}
                      </button>;
              })}
                </div>
                <span className="text-xs text-slate-400" data-id="element-3035" data-mp="3scjq2dtnfxa7">
                  {t('Rédacteur', 'Drafted by')}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-3 shrink-0" data-id="element-3036" data-mp="3ilgshlzxi0fo">
              <span className="text-xs text-slate-500 text-right max-w-[110px]" data-id="element-3037" data-mp="3ijm4tzx9vu7w">
                {t('Signature électronique requise', 'E-signature required')}
              </span>
              <button type="button" role="switch" aria-checked={tpl.eSignature} aria-label={t('Signature électronique requise', 'E-signature required')} onClick={() => setTemplates(prev => prev.map(item => item.id === tpl.id ? {
            ...item,
            eSignature: !item.eSignature
          } : item))} className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:ring-offset-1 ${tpl.eSignature ? 'bg-[#4F46E5]' : 'bg-slate-200'}`} data-id="element-3038" data-mp="f4gpv0osqhzb">
                <span aria-hidden="true" className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${tpl.eSignature ? 'translate-x-4' : 'translate-x-0'}`} data-id="element-3039" data-mp="2ic407hh19owz" />
              </button>
            </div>
          </li>)}
      </ul>
    </section>;
}

// --- PAYMENTS ---------------------------------------------------------------

type TxKind = 'inscription' | 'camp' | 'location';
export function PaymentsPanel() {
  const {
    t
  } = useLanguage();
  const [methods, setMethods] = useState<Record<TxKind, string[]>>({
    inscription: ['credit', 'interac', 'comptoir'],
    camp: ['credit', 'interac', 'comptoir'],
    location: ['interac', 'comptoir']
  });
  const [holdHours, setHoldHours] = useState('48');
  const [reconciliation, setReconciliation] = useState<'auto' | 'manuelle'>('auto');
  const transactions: {
    id: TxKind;
    label: string;
    hint: string;
  }[] = [{
    id: 'inscription',
    label: t('Inscription à une activité', 'Activity registration'),
    hint: t('paiement au moment de l’inscription', 'paid at registration')
  }, {
    id: 'camp',
    label: t('Camp de jour', 'Day camp'),
    hint: t('paiement par versements possible', 'installments allowed')
  }, {
    id: 'location',
    label: t('Location de plateau', 'Facility rental'),
    hint: t('facturation après signature', 'billed after signature')
  }];
  const available = [{
    id: 'credit',
    label: t('Carte de crédit', 'Credit card'),
    icon: CreditCardIcon
  }, {
    id: 'interac',
    label: t('Virement Interac', 'Interac transfer'),
    icon: SmartphoneIcon
  }, {
    id: 'comptoir',
    label: t('Comptoir', 'Front counter'),
    icon: StoreIcon
  }];
  const toggle = (tx: TxKind, method: string) => setMethods(prev => ({
    ...prev,
    [tx]: prev[tx].includes(method) ? prev[tx].filter(m => m !== method) : [...prev[tx], method]
  }));
  const field = 'w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5]';
  return <div className="space-y-4" data-id="element-3040" data-mp="26wxu8792q4ue">
      <section className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-3041" data-mp="2bv98qvabd3iy">
        <h2 className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-3042" data-mp="2bt51q4h4boeg">
          {t('Modes de paiement acceptés', 'Accepted payment methods')}
        </h2>
        <p className="text-sm text-slate-500 mt-1 mb-5 max-w-2xl" data-id="element-3043" data-mp="hi9im88b4qxf">
          {t('Chaque type de transaction peut accepter des modes différents. Un mode désactivé disparaît du parcours citoyen.', 'Each transaction type can accept different methods. A disabled method disappears from the citizen journey.')}
        </p>

        <ul className="space-y-2" data-id="element-3044" data-mp="3re733xldncff">
          {transactions.map(tx => <li key={tx.id} className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 px-4 py-3.5 flex-wrap" data-id="element-3045" data-mp="3rg0878gcdolb">
              <div className="min-w-0" data-id="element-3046" data-mp="nyrsov9370tr">
                <p className="text-sm font-medium text-slate-800" data-id="element-3047" data-mp="iydltgj81wxb">{tx.label}</p>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-3048" data-mp="j0glksa6f4f8">{tx.hint}</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap" data-id="element-3049" data-mp="ewin8jsodxn8">
                {available.map(method => {
              const Icon = method.icon;
              const on = methods[tx.id].includes(method.id);
              return <button key={method.id} onClick={() => toggle(tx.id, method.id)} aria-pressed={on} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${on ? 'border-[#4F46E5] bg-indigo-50 text-[#4F46E5]' : 'border-slate-200 bg-white text-slate-400 hover:bg-slate-50'}`} data-id="element-3050" data-mp="rdt3oromcp12">
                      <Icon className="w-3.5 h-3.5" data-id="element-3051" data-mp="1f3476gxcsxiw" />
                      {method.label}
                      {on && <CheckIcon className="w-3 h-3" data-id="element-3052" data-mp="113uuhr8cwljo" />}
                    </button>;
            })}
              </div>
            </li>)}
        </ul>
      </section>

      {/* Interac — highlighted request from the City */}
      <section className="bg-white rounded-xl border-2 border-[#4F46E5]/30 p-6" data-id="element-3053" data-mp="3pwglbc43h57c">
        <div className="flex items-start gap-3 mb-5" data-id="element-3054" data-mp="3ic5w713mnvu8">
          <span className="w-9 h-9 rounded-lg bg-indigo-50 text-[#4F46E5] flex items-center justify-center shrink-0" data-id="element-3055" data-mp="3iauwpdh7ta8x">
            <SmartphoneIcon className="w-4 h-4" data-id="element-3056" data-mp="3dffl6s6ojqfo" />
          </span>
          <div data-id="element-3057" data-mp="3s9antgg8sjif">
            <h2 className="text-lg font-bold text-[#0F172A] font-heading flex items-center gap-2" data-id="element-3058" data-mp="3s9u4ktu5p1uv">
              {t('Virement Interac', 'Interac transfer')}
              <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-[#4F46E5] text-xs font-semibold border border-indigo-100" data-id="element-3059" data-mp="2zl514o5mokfy">
                {t('activé', 'enabled')}
              </span>
            </h2>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl" data-id="element-3060" data-mp="39cswh05b2lmp">
              {t('Le citoyen reçoit une demande de fonds et règle depuis son application bancaire, sans carte de crédit.', 'The citizen receives a request for money and pays from their banking app, with no credit card.')}
            </p>
          </div>
        </div>

        {/* Citizen journey */}
        <ol className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6" data-id="element-3061" data-mp="31jps8n8ugbd2">
          {[{
          icon: SmartphoneIcon,
          title: t('1. Demande de fonds', '1. Request for money'),
          text: t('Une demande Interac est envoyée au courriel du citoyen dès la réservation.', 'An Interac request is emailed to the citizen as soon as the booking is made.')
        }, {
          icon: ArrowRightIcon,
          title: t('2. Application bancaire', '2. Banking app'),
          text: t('Le citoyen est redirigé vers son institution et approuve le virement.', 'The citizen is redirected to their institution and approves the transfer.')
        }, {
          icon: CheckIcon,
          title: t('3. Place confirmée', '3. Spot confirmed'),
          text: t('Le dépôt reçu confirme la place et le reçu est transmis automatiquement.', 'The received deposit confirms the spot and the receipt is sent automatically.')
        }].map(step => {
          const Icon = step.icon;
          return <li key={step.title} className="rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-4" data-id="element-3062" data-mp="1hobrn8lr62ok">
                <span className="w-8 h-8 rounded-lg bg-white border border-slate-200 text-[#4F46E5] flex items-center justify-center mb-2.5" data-id="element-3063" data-mp="1dob9bcvj97c8">
                  <Icon className="w-4 h-4" data-id="element-3064" data-mp="13okc33fl8e8g" />
                </span>
                <p className="text-sm font-semibold text-slate-800" data-id="element-3065" data-mp="13osq4yng5pl8">
                  {step.title}
                </p>
                <p className="text-xs text-slate-500 mt-1" data-id="element-3066" data-mp="yrtx6jfiroom">{step.text}</p>
              </li>;
        })}
        </ol>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3067" data-mp="2t2yxbdrediz7">
          <div data-id="element-3068" data-mp="2t1nxtq4zixdw">
            <label htmlFor="hold-hours" className="block text-xs font-medium text-slate-600 mb-1" data-id="element-3069" data-mp="21yeiq8otlrmm">
              <span className="inline-flex items-center gap-1.5" data-id="element-3070" data-mp="26vwjhbsop0jj">
                <ClockIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-3071" data-mp="1t6ck8fybalez" />
                {t('Délai avant libération automatique de la place', 'Delay before the spot is released automatically')}
              </span>
            </label>
            <div className="flex items-center gap-2" data-id="element-3072" data-mp="1o6q7s8qb5978">
              <input id="hold-hours" type="number" min="1" value={holdHours} onChange={e => setHoldHours(e.target.value)} className={`${field} max-w-[100px]`} data-id="element-3073" data-mp="1o8b2pzgjau5m" />
              <span className="text-sm text-slate-500" data-id="element-3074" data-mp="34kq4zbxrqkqs">
                {t('heures', 'hours')}
              </span>
            </div>
            <p className="text-xs text-slate-400 mt-1" data-id="element-3075" data-mp="38ls6jzf5r8vx">
              {t('Passé ce délai sans virement reçu, la place retourne au catalogue.', 'After this delay without a received transfer, the spot returns to the catalogue.')}
            </p>
          </div>

          <div data-id="element-3076" data-mp="16r453iq64yny">
            <span className="block text-xs font-medium text-slate-600 mb-1" data-id="element-3077" data-mp="16qvp3hyr1d8j">
              <span className="inline-flex items-center gap-1.5" data-id="element-3078" data-mp="11wzu8ods86hl">
                <RefreshCwIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-3079" data-mp="1gm5wkl7x2ctp" />
                {t('Mode de réconciliation', 'Reconciliation mode')}
              </span>
            </span>
            <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden" data-id="element-3080" data-mp="1pof37002dm1t">
              {[{
              id: 'auto' as const,
              label: t('Automatique', 'Automatic')
            }, {
              id: 'manuelle' as const,
              label: t('Manuelle', 'Manual')
            }].map(option => <button key={option.id} onClick={() => setReconciliation(option.id)} className={`px-3.5 py-2 text-sm font-medium transition-colors ${reconciliation === option.id ? 'bg-[#0F172A] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-id="element-3081" data-mp="3mi043wo44hdb">
                  {option.label}
                </button>)}
            </div>
            <p className="text-xs text-slate-400 mt-1" data-id="element-3082" data-mp="2e5pla4wz5g7v">
              {reconciliation === 'auto' ? t('Le dépôt confirmé par l’institution marque la transaction payée.', 'A deposit confirmed by the institution marks the transaction as paid.') : t('Un employé valide chaque virement depuis la file « Virements Interac à réconcilier ».', 'A staff member validates each transfer from the “Interac transfers to reconcile” queue.')}
            </p>
          </div>
        </div>

        <p className="flex items-start gap-2 mt-5 rounded-lg border border-indigo-100 bg-indigo-50/60 px-4 py-3 text-sm text-[#3730A3]" data-id="element-3083" data-mp="avunke86libr">
          <SparklesIcon className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-3084" data-mp="fmb3c4zh5g1a" />
          {t('Tant que le virement n’est pas reçu, la transaction reste au statut « Virement attendu » — visible partout dans le module.', 'Until the transfer is received, the transaction stays in the “Transfer pending” state — visible throughout the module.')}
        </p>
      </section>
    </div>;
}